import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import jwt_decode from 'jwt-decode';
import { ToastrService } from 'ngx-toastr';
import { EMPTY, of } from 'rxjs';
import { concatMap } from 'rxjs/internal/operators/concatMap';
import { catchError, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { HttpService } from '../../services/http.service';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { EmailConnectionActionTypes } from '../actions/email-connection.actions';
import { ResolverLoadUser } from '../actions/resolver.actions';
import { UsersActionTypes } from '../actions/users.actions';
import { State } from '../index';
import { getUser } from '../selectors/users.selectors';
import { DecodedTokenType } from '../../entities/token.entity';
import { getToken } from '../selectors/base.selectors';
import { BroadcastService } from '../../../services/broadcast.service';
import { TwoFactorMethodEnum } from '../../entities/user.entity';
import { stringifyIfNotNullOrUndefined } from '../../../helper/helper';
import { LocalStorageService } from '../../../services/local-storage.service';
enum ErrorCodes {
    Login = 'Keinen Benutzer mit dieser Kombination aus E-Mail und Passwort gefunden',
    MFA = 'Zwei-Faktor-Authentifizierung fehlgeschlagen',
    NEWEMail = 'E-Mail senden fehlgeschlagen'
}

@Injectable()
export class DataEffects {

    Error$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.ErrorAction),
        withLatestFrom(this.store$.select(getUser), this.store$.select(getToken)),
        tap(([action, user, token]) => {
            console.warn(action.Payload.ToasterMessage);
            if (action.Payload.Err) {
                console.error(action.Payload.Err);
                if (action.Payload.Caught) {
                    console.error(action.Payload.Caught);
                }
            }
            const error = new Error(action.Payload.Err);
            error.message = action.Payload.ToasterMessage;
            Sentry.captureException(error, {
                user: {
                    id: user?.Id + '',
                    ip_address: '',
                    email: user?.Email,
                    username: user?.DisplayName,
                    userEntity: user,
                },
                level: 'error',
                contexts: {
                    auth: { Bearer: token },
                },
                extra: {
                    ...action.Payload,
                },
            });
            this.apiToasterService.error(action.Payload.ToasterMessage, '', { timeOut: 5000 });
        }),
    ), { dispatch: false });

    CheckToken$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.CheckToken),
        switchMap(() =>
            this.gatewayHttpService.graphQl({ query: `query{login{token}}` }, { token: localStorage.getItem('token') }).pipe(
                map((res: any) => {
                    if (res && res.login && res.login.token) {
                        return BaseActionTypes.UpdateToken({ Payload: res.login.token });
                    } else {
                        localStorage.removeItem('token');
                        window.location.href = '/login';
                        return BaseActionTypes.ErrorAction({
                            Payload: { ToasterMessage: 'Login kann nicht verifiziert werden!' },
                        });
                    }
                }),
                catchError((err, caught) => {
                    this.apiToasterService.error('Loginversuch fehlgeschlagen');
                    return EMPTY;
                }),
            ),
        ),
    ));

    loginFail$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.PerformLoginFailure),
        map(({Payload}) => {
            this.store$.dispatch(BaseActionTypes.UpdateToken({ Payload: '' }));
            return BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Login } });
        }),
    ));

    loginSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.PerformLoginSuccess),
        tap(({Payload}) => {
            if (Payload.token) {
                this.bc.sendLogin(Payload.token)
                this.store$.dispatch(BaseActionTypes.UpdateToken({ Payload: Payload.token }));
            }
        }),
    ), {dispatch: false});
    PerformLogin$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.PerformLoginRequest),
        switchMap(action => {
            const queryString = `
            query {
                login(
                    username: ${JSON.stringify(action.Payload.Username)}
                    password: ${JSON.stringify(action.Payload.Password)}
                    ${stringifyIfNotNullOrUndefined(action.Payload, 'trustedDeviceToken')}
                ) {
                    token
                    challengeId
                    twoFactorMethod
                }
            }`;
            return this.gatewayHttpService.graphQl({ query: queryString }, null).pipe(
                map(res => {
                    if (res && res.login && (res.login.token || (res.login.challengeId && res.login.twoFactorMethod))) {
                        return BaseActionTypes.PerformLoginSuccess({
                            Payload: {
                                challengeId: res.login.challengeId,
                                twoFactorMethod: res.login.twoFactorMethod,
                                token: res.login.token,
                            }
                        });
                    } else {
                        return BaseActionTypes.PerformLoginFailure({Payload: { ToasterMessage: ErrorCodes.Login, Errors: 'wrong response' }})
                    }
                }),
                catchError((err, caught) => of(BaseActionTypes.PerformLoginFailure({
                        Payload: {
                            ToasterMessage: ErrorCodes.Login,
                            Errors: err,
                        },
                    })),
                ),
            );
        }),
    ));

    mfaFail$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.PerformMFAFailure),
        map(({Payload}) => {
            return BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Login } });
        }),
    ));
    mfaSuccess$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.PerformMFASuccess),
        tap(({Payload}) => {
            if (Payload.token) {
                this.bc.sendLogin(Payload.token)
            }
            this.store$.dispatch(BaseActionTypes.UpdateToken({ Payload: Payload.token }));
            const email = GetDecodeAccessToken(Payload.token).email
            console.log('###email',email)
            if (email) {
                this.localStorageService.setTrustedDeviceToken(email, Payload.trustedDeviceToken);
            }
        }),
    ), {dispatch: false});
    PerformMFA$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.PerformMFARequest),
        switchMap(({Payload}) => {
            const queryString = `
mutation {
  verifyTwoFactor(
    code: "${Payload.code}",
    challengeId: "${Payload.challengeId}",
    trustThisDevice: ${Payload.trustThisDevice},
  ) {
    token
    trustedDeviceToken
  }
}`;
            return this.gatewayHttpService.graphQl({ query: queryString }, null).pipe(
                map(res => {
                    if (res && res.verifyTwoFactor && res.verifyTwoFactor.token) {
                        return BaseActionTypes.PerformMFASuccess({
                            Payload: {
                                token: res.verifyTwoFactor.token,
                                trustedDeviceToken: res.verifyTwoFactor.trustedDeviceToken,
                            }
                        });
                    } else {
                        return BaseActionTypes.PerformMFAFailure({Payload: { ToasterMessage: ErrorCodes.MFA, Errors: 'wrong response' }})
                    }
                }),
                catchError((err, caught) => of(BaseActionTypes.PerformMFAFailure({
                        Payload: {
                            ToasterMessage: ErrorCodes.MFA,
                            Errors: err,
                        },
                    })),
                ),
            );
        }),
    ));

    SendNewMFAMail$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.SendNewMFAMail),
        switchMap(({Payload}) => {
            const queryString = `
mutation {
  resendOtpEmail(
    challengeId: "${Payload.challengeId}",
  ) {
    challengeId
  }
}`;
            return this.gatewayHttpService.graphQl({ query: queryString }, null).pipe(
                map(res => {
                    if (res?.resendOtpEmail?.challengeId) {
                        this.apiToasterService.success('E-Mail erfolgreich gesendet');

                        return BaseActionTypes.SendNewMailSuccess({
                            Payload: {
                                challengeId: res.resendOtpEmail.challengeId,
                            },
                        });
                    }

                    return BaseActionTypes.SendNewMailFailure({
                        Payload: {
                            ToasterMessage: ErrorCodes.NEWEMail,
                            Errors: 'wrong response',
                        },
                    });
                }),
                catchError(err => of(BaseActionTypes.SendNewMailFailure({
                    Payload: {
                        ToasterMessage: ErrorCodes.NEWEMail,
                        Errors: err,
                    },
                }))),
            );
        }),


    ));

    UpdateToken$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.UpdateToken),
        map(action => {
            this.localStorageService.token = action.Payload;
            const decToken = this.GetDecodeAccessToken(action.Payload);
            return decToken
                ? new ResolverLoadUser({ id: +decToken.userId })
                : UsersActionTypes.UpdateUser({
                      Payload: null,
                  });
        }),
    ));

    UpdateUserForErrorFandling$ = createEffect(() => this.actions$.pipe(
        ofType(UsersActionTypes.UpdateUser),
        tap(({Payload}) => {
            if (Payload) {
                Sentry.setUser({
                    id: Payload.Id,
                    ip_address: '',
                    email: Payload.Email,
                    username: Payload.DisplayName,
                });
                Sentry.setTag('partnerId', Payload.PartnerId+'')
            } else {
                Sentry.setUser(null);
                Sentry.setTag('partnerId', '')
            }
        })
    ), { dispatch: false });

    ChangePassword$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.ChangePassword),
        withLatestFrom(this.store$),
        switchMap(([value, store]) =>
            this.gatewayHttpService
                .graphQl(
                    {
                        query: `
            mutation{
                changePassword(oldPassword: ${JSON.stringify(value.Payload.oldPassword)}, newPassword: ${JSON.stringify(value.Payload.newPassword)}) {
                    id
                }
            }`,
                    },
                    { token: store.base.token },
                )
                .pipe(
                    map(res => {
                        if (res.changePassword?.id) {
                            this.apiToasterService.success('Passwort wurde geändert');
                        } else {
                            this.apiToasterService.error('Passwort konnte nicht geändert werden');
                        }
                        return EMPTY;
                    }),
                    catchError((err, caught) => {
                        console.error(err, caught);
                        this.apiToasterService.error('Passwort konnte nicht geändert werden');
                        return EMPTY;
                    }),
                ),
        ),
    ), { dispatch: false });

    ResetPassword$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.ResetPassword),
        switchMap(action =>
            this.gatewayHttpService
                .graphQl(
                    {
                        query: `mutation{resetUser(email: ${JSON.stringify(action.Payload.Email)})}`,
                    },
                    null,
                )
                .pipe(
                    map(() => {
                        this.apiToasterService.success('Password wurde erfolgreich zurückgesetzt!');
                    }),
                    catchError((err, caught) => {
                        console.error(err, caught);
                        this.apiToasterService.error('Passwort konnte nicht zurückgesetzt werden');
                        return EMPTY;
                    }),
                ),
        ),
    ), { dispatch: false });

    ActivateUser$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.ActivateUser),
        switchMap(action =>
            this.gatewayHttpService
                .graphQl(
                    {
                        query: `
            mutation{
                activateUser(
                    verificationCode: ${JSON.stringify(action.Payload.verificationCode)},
                    password: ${JSON.stringify(action.Payload.password)},
                    userId: ${JSON.stringify(action.Payload.userId)}) {
                        id
                    }
            }`,
                    },
                    null,
                )
                .pipe(
                    map((ret: any) => {
                        if (ret && ret.activateUser && ret.activateUser.id > 0) {
                            this.apiToasterService.success('Konto wurde erfolgreich aktiviert!');
                            return EMPTY;
                        } else {
                            this.apiToasterService.error('Konto konnte nicht aktiviert werden! Bitte wenden Sie sich an den Administrator.');
                            return EMPTY;
                        }
                    }),
                    catchError(() => {
                        this.apiToasterService.error('Konto konnte nicht aktiviert werden! Bitte wenden Sie sich an den Administrator.');
                        return EMPTY;
                    }),
                ),
        ),
    ), { dispatch: false });

    SetupEmailOtp$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.SetupMailOtpRequest),
        switchMap(() => this.gatewayHttpService.graphQl({query: `
        mutation {
          setupOtp(twoFactorMethod: ${TwoFactorMethodEnum.EmailOtp}) {
            twoFactorMethod
          }
        }`}).pipe(
            map(ret => {
                if (ret && ret.setupOtp && ret.setupOtp.twoFactorMethod) {
                    return BaseActionTypes.SetupMailOtpSuccess({Payload: ret.setupOtp});
                } else {
                    return BaseActionTypes.SetupMailOtpFailure({
                        Payload: {
                            Errors: null,
                            ToasterMessage: 'E-Mail 2FA konnte nicht aktiviert werden',
                        }
                    });
                }
            })
        ))
    ))
    ResetOtp$ = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.RemoveOTPRequest),
        switchMap(() => this.gatewayHttpService.graphQl({query: `
        mutation {
          setupOtp(twoFactorMethod: ${TwoFactorMethodEnum.None}) {
            twoFactorMethod
          }
        }`}).pipe(
            map(ret => {
                if (ret && ret.setupOtp && ret.setupOtp.twoFactorMethod) {
                    return BaseActionTypes.RemoveOTPSuccess();
                } else {
                    return BaseActionTypes.RemoveOTPFailure({
                        Payload: {
                            Errors: null,
                            ToasterMessage: '2FA konnte nicht entfernt werden',
                        }
                    });
                }
            })
        ))
    ))

    failures = createEffect(() => this.actions$.pipe(
        ofType(BaseActionTypes.SetupMailOtpFailure, BaseActionTypes.RemoveOTPFailure),
        map(action => BaseActionTypes.ErrorAction({
            Payload: {
                ToasterMessage: action.Payload.ToasterMessage,
                Err: action.Payload.Errors,
            }
        }))
    ));

    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private apiToasterService: ToastrService, private gatewayHttpService: HttpService, private bc: BroadcastService, private localStorageService: LocalStorageService) {}
    GetDecodeAccessToken(token: string) {
        return GetDecodeAccessToken(token);
    }

}
export interface AppDecodedTokenType extends DecodedTokenType {
    email: string;
}
function GetDecodeAccessToken(token: string): AppDecodedTokenType {
    try {
        return jwt_decode<AppDecodedTokenType>(token);
    } catch (e) {
        return null;
    }
}
